import { Phone, MessageSquare, Calendar } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { PatientListItem } from '../../types/analytics.types';
import { formatDate, formatPhoneNumber } from '../../lib/formatters';

interface PatientDetailPanelProps {
  patient: PatientListItem | null;
  open: boolean;
  onClose: () => void;
}

export function PatientDetailPanel({
  patient,
  open,
  onClose,
}: PatientDetailPanelProps) {
  if (!patient) return null;

  const getRiskColor = (score?: number) => {
    if (!score) return 'bg-muted text-foreground';
    if (score >= 3) return 'bg-destructive/10 text-destructive';
    if (score >= 2) return 'bg-warning/10 text-warning';
    return 'bg-success/10 text-success';
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:w-[540px] sm:max-w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {patient.firstName} {patient.lastName}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="flex items-center gap-2">
            <Badge variant="outline">MRN: {patient.mrn}</Badge>
            {patient.riskScore && (
              <Badge className={getRiskColor(patient.riskScore)}>
                Risk: {patient.riskScore.toFixed(1)}
              </Badge>
            )}
            {patient.smsCapable && (
              <Badge variant="secondary" className="bg-success/10 text-success">
                SMS
              </Badge>
            )}
          </div>

          <div className="grid gap-4">
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">
                Demographics
              </h4>
              <div className="space-y-1 text-sm">
                <p>
                  <span className="text-muted-foreground">Age:</span> {patient.age} years
                </p>
                <p>
                  <span className="text-muted-foreground">Gender:</span> {patient.gender}
                </p>
                <p>
                  <span className="text-muted-foreground">DOB:</span>{' '}
                  {formatDate(patient.dateOfBirth)}
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">
                Care Team
              </h4>
              <div className="space-y-1 text-sm">
                {patient.pcp && (
                  <p>
                    <span className="text-muted-foreground">PCP:</span> {patient.pcp}
                  </p>
                )}
                {patient.careManager && (
                  <p>
                    <span className="text-muted-foreground">Care Manager:</span>{' '}
                    {patient.careManager}
                  </p>
                )}
                {patient.payer && (
                  <p>
                    <span className="text-muted-foreground">Payer:</span> {patient.payer}
                  </p>
                )}
              </div>
            </div>

            {patient.phone && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Contact
                </h4>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  {formatPhoneNumber(patient.phone)}
                  {!patient.smsCapable && (
                    <span className="text-xs text-muted-foreground">(No SMS)</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-4 border-t">
            <Button className="flex-1">
              <Phone className="h-4 w-4 mr-2" />
              Call
            </Button>
            {patient.smsCapable && (
              <Button variant="outline" className="flex-1">
                <MessageSquare className="h-4 w-4 mr-2" />
                SMS
              </Button>
            )}
            <Button variant="outline" className="flex-1">
              <Calendar className="h-4 w-4 mr-2" />
              Schedule
            </Button>
          </div>

          <Button variant="outline" className="w-full">
            View Full Patient Record
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
